import { crack, groundRing, palm, sprite, star, waves } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ฝ่ามือมังกรทองเก้าสวรรค์ (ศิลาเก้ากระบี่, yang): the stone school's hidden palm. A gold dragon with
 * amber shadow and cream highlights leaps out of the palm; cloud bands for the nine heavens.
 */

const GOLD = '#ffcf5a';
const AMBER = '#c8862a';
const CREAM = '#fff4c8';
const CLOUD = '#f4ecd8';
const CLOUD_SH = '#c8b48a';

const goldPalm = (p: Pix, x: number, y: number, o: { flip?: boolean; flipY?: boolean } = {}) =>
  palm(p, x, y, C.skin, { ...o, lines: C.skinDark });

/** The gold dragon's head facing right, large (16×12): swept horns, mane, whisker, open jaw. */
function bigHead(p: Pix, x: number, y: number, o: { flip?: boolean; flipY?: boolean } = {}): void {
  sprite(
    p,
    x,
    y,
    [
      '.aa.............',
      'aaaa..aa........',
      '.aaaaaaaa.......',
      '..ggggggaa......',
      '.gggggggggg.....',
      'ggggegggggggggw.',
      'gggggggggggggggw',
      'agggggggammmmm..',
      'aaggggga..wwww..',
      '.agggggggggggg..',
      '..aggggggggggw..',
      '...aa..cc..c....',
    ],
    { a: AMBER, g: GOLD, e: CREAM, m: AMBER, w: CREAM, c: CREAM },
    o,
  );
}

/** The gold dragon's head facing right (12×9): swept horns, whisker, open jaw. */
function goldHead(p: Pix, x: number, y: number, o: { flip?: boolean; flipY?: boolean } = {}): void {
  sprite(
    p,
    x,
    y,
    [
      'aa..........',
      '.aaa..aa....',
      '..gggggaa...',
      '.gggegggggw.',
      'ggggggggggg.',
      'gggggammmmm.',
      '.ggggggwww..',
      '..ggggggggg.',
      '..aa....cc..',
    ],
    { a: AMBER, g: GOLD, e: CREAM, m: AMBER, w: CREAM, c: CREAM },
    o,
  );
}

/** A dragon body as a thick gold ribbon with amber belly dots. */
function body(p: Pix, pts: readonly (readonly [number, number])[], w = 3): void {
  p.path(pts, GOLD, w);
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1]!;
    const [x1, y1] = pts[i]!;
    p.px((x0 + x1) / 2, (y0 + y1) / 2 + 1, AMBER);
  }
}

/** A flat cloud band. */
function band(p: Pix, x: number, y: number, w: number): void {
  p.rect(x, y, w, 2, CLOUD_SH);
  p.rect(x + 1, y - 1, w - 3, 2, CLOUD);
  p.ell(x + w * 0.35, y - 1, 2.2, 1.6, CLOUD);
}

registerIcons('skill', {
  // A close push; the gold dragon bursts from the palm.
  'dp9-burst': (p) => {
    goldPalm(p, 1, 12);
    bigHead(p, 14, 9);
    waves(p, 12, 18, 3, 1, CREAM, 0.9, 3);
    star(p, 28, 9, 2, CREAM);
  },
  // One dragon straight as a lance at a far mark.
  'dp9-lance': (p) => {
    body(p, [
      [2, 17],
      [8, 15],
      [13, 17],
    ]);
    goldHead(p, 13, 11);
    p.rect(26, 15, 3, 1, CREAM);
    p.rect(2, 22, 8, 1, AMBER).rect(5, 11, 6, 1, AMBER);
  },
  // Three small dragons arcing through cloud one after another.
  'dp9-twin': (p) => {
    band(p, 3, 25, 26);
    for (const [x, y] of [
      [3, 18],
      [11, 11],
      [19, 4],
    ] as const) {
      body(p, [
        [x, y + 6],
        [x + 3, y + 4],
      ], 2);
      sprite(p, x + 3, y, ['aa.....', '.gggg..', 'gegggw.', 'ggammm.', '.ggggg.'], { a: AMBER, g: GOLD, e: CREAM, m: AMBER, w: CREAM });
    }
  },
  // The dragon's head bites down from above through the guard.
  'dp9-rend': (p) => {
    band(p, 3, 4, 14);
    body(p, [
      [10, 1],
      [12, 5],
    ], 4);
    bigHead(p, 3, 6);
    crack(p, [
      [12, 18],
      [10, 22],
      [13, 24],
      [11, 29],
    ], AMBER, 2);
    p.rect(3, 18, 18, 1, CLOUD_SH);
  },
  // A stamp that wakes the dragon: a footfall, cracks, the roar rising.
  'dp9-stomp': (p) => {
    sprite(p, 6, 9, ['.bbbb....', '.bbbb....', '.bbbb....', '.bbbb....', '.bbbbb...', 'bbbbbbbbb', 'bbbbbbbbb', 'ddddddddd'], { b: '#6a4a3a', d: '#3a2a24' }, { k: 1 });
    p.rect(3, 24, 26, 2, AMBER);
    crack(p, [
      [12, 24],
      [9, 28],
    ], CREAM);
    crack(p, [
      [16, 24],
      [20, 29],
    ], CREAM);
    waves(p, 13, 13, 9, 2, GOLD, 0.7, 4);
    bigHead(p, 15, 2);
  },
  // The dragon coils once round its caster.
  'dp9-whirl': (p) => {
    groundRing(p, 16, 20, 12, 7, AMBER);
    p.ring(16, 18, 12, 8, GOLD, -Math.PI * 0.2, Math.PI * 1.3);
    p.ring(16, 18, 11, 7, CREAM, Math.PI * 0.1, Math.PI * 0.9);
    goldHead(p, 18, 4);
    p.ell(16, 18, 2, 3, C.skin);
  },
  // Two palm beats, two dragons out one after the other.
  'dp9-double': (p) => {
    goldPalm(p, 1, 2);
    goldPalm(p, 1, 17);
    goldHead(p, 16, 3);
    goldHead(p, 16, 18);
  },
  // The great dragon comes down from nine heavens onto the chosen ground.
  'dp9-sky': (p) => {
    for (const y of [5, 9, 13]) band(p, 4 + (y % 4), y, 9);
    body(p, [
      [26, 3],
      [22, 8],
      [24, 12],
      [19, 16],
    ], 4);
    bigHead(p, 4, 12, { flip: true });
    groundRing(p, 16, 26, 12, 3, GOLD);
    star(p, 5, 24, 2, CREAM);
    star(p, 27, 24, 2, CREAM);
  },
});
